/**
 * API envelope and pagination — dev brief §3 (house conventions) and §10.
 *
 * Every response, success or failure, has the same shape:
 *
 *   success   { "message": "Success", "data": { … } }
 *   failure   { "message": "<error>", "data": null, "errors": { "field": ["…"] } }
 *
 * The SPA can therefore treat `errors` as the only thing it ever needs to bind
 * to a form, and `message` as the only thing it ever needs to toast.
 */

export const SUCCESS_MESSAGE = 'Success';

export interface SuccessResponse<T> {
  message: typeof SUCCESS_MESSAGE | string;
  data: T;
}

/** Field-keyed validation errors, as produced by Zod. */
export type FieldErrors = Record<string, string[]>;

export interface ErrorResponse {
  message: string;
  data: null;
  errors?: FieldErrors;
  /** Stable machine-readable code, for clients that must branch on the cause. */
  code?: ApiErrorCode;
}

export type ApiResponse<T> = SuccessResponse<T> | ErrorResponse;

export type ApiErrorCode =
  | 'UNAUTHENTICATED'
  | 'TOKEN_EXPIRED'
  | 'FORBIDDEN_ROLE'
  | 'FORBIDDEN_SITE'
  | 'NOT_FOUND'
  | 'VALIDATION_FAILED'
  | 'CONFLICT'
  | 'CODE_LOCKED'
  | 'POSITION_OCCUPIED'
  | 'RATE_LIMITED'
  | 'ACCOUNT_LOCKED'
  | 'IDEMPOTENCY_REPLAY'
  | 'PAYLOAD_TOO_LARGE'
  | 'INTERNAL';

/** Default HTTP status for each error code, so handlers do not re-decide. */
export const ERROR_STATUS: Record<ApiErrorCode, number> = {
  UNAUTHENTICATED: 401,
  TOKEN_EXPIRED: 401,
  FORBIDDEN_ROLE: 403,
  // Deliberately 404, not 403: a cross-tenant or cross-site probe must not be
  // able to confirm that a record exists (§9 rule 3).
  FORBIDDEN_SITE: 404,
  NOT_FOUND: 404,
  VALIDATION_FAILED: 422,
  CONFLICT: 409,
  CODE_LOCKED: 409,
  POSITION_OCCUPIED: 409,
  RATE_LIMITED: 429,
  ACCOUNT_LOCKED: 423,
  IDEMPOTENCY_REPLAY: 409,
  PAYLOAD_TOO_LARGE: 413,
  INTERNAL: 500,
};

export function success<T>(data: T, message: string = SUCCESS_MESSAGE): SuccessResponse<T> {
  return { message, data };
}

export function failure(
  message: string,
  opts: { errors?: FieldErrors; code?: ApiErrorCode } = {},
): ErrorResponse {
  const body: ErrorResponse = { message, data: null };
  if (opts.errors) body.errors = opts.errors;
  if (opts.code) body.code = opts.code;
  return body;
}

// ---------------------------------------------------------------------------
// Cursor pagination
// ---------------------------------------------------------------------------

/**
 * Cursor, not offset. A 30-year-old tree accumulates 800–1,500 timeline
 * entries and a 10,000-tree site is a long position list; `OFFSET 9000` makes
 * SQLite walk 9,000 rows it will then discard, and rows shifting under a
 * paginating reader is how a manager sees the same entry twice.
 */
export interface Page<T> {
  items: T[];
  /** Opaque; pass back as `?cursor=`. `null` when there is no further page. */
  nextCursor: string | null;
  /** Only set when a total is cheap to compute; never assume it is present. */
  total?: number;
}

export const DEFAULT_PAGE_SIZE = 50;
/** The timeline is denser and image-heavy, so it pages smaller (§10). */
export const TIMELINE_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 200;

export function clampLimit(raw: unknown, fallback: number = DEFAULT_PAGE_SIZE): number {
  const n = typeof raw === 'string' ? Number.parseInt(raw, 10) : typeof raw === 'number' ? raw : NaN;
  if (!Number.isFinite(n) || n < 1) return fallback;
  return Math.min(Math.trunc(n), MAX_PAGE_SIZE);
}

/**
 * Cursors are base64url of a JSON tuple. Opaque to clients by contract, but
 * inspectable in a debugger, which is worth more than the few bytes an opaque
 * binary encoding would save.
 */
export function encodeCursor(parts: (string | number)[]): string {
  const json = JSON.stringify(parts);
  const b64 = btoa(String.fromCharCode(...new TextEncoder().encode(json)));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeCursor(cursor: string): (string | number)[] | null {
  try {
    const b64 = cursor.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return Array.isArray(parsed) ? (parsed as (string | number)[]) : null;
  } catch {
    return null;
  }
}
